import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { User, Mail, Lock, Phone, Calendar, Users, ArrowLeft, ArrowRight, Check, Home, Route, MailCheck, Car, Palette, Hash } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { LanguageFlags } from '../components/LanguageFlags';
import { AIErrorHelper } from '../components/AIErrorHelper';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { detectSegment, SEGMENT_LABELS, getAllMakes, getModelsForMake } from '../utils/vehicleSegments';
import { API } from '../utils/apiConfig';
import './Login.css';

const DAYS = [
  { key: 0, short: 'Mo' }, { key: 1, short: 'Di' }, { key: 2, short: 'Mi' },
  { key: 3, short: 'Do' }, { key: 4, short: 'Fr' }, { key: 5, short: 'Sa' }, { key: 6, short: 'So' },
];

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const { register, isAuthenticated, logout } = useAuth();

  // Track if the user came from the Orb welcome screen — if yes, the
  // magic-link must redirect them back into /orb-app after PIN setup
  // (not the regular /dashboard).
  const sourceParam = (params.get('source') || '').toLowerCase();
  const source = sourceParam === 'orb' ? 'orb' : 'webapp';

  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  // Already-authenticated users have no business in the registration wizard —
  // and any browser-back navigation from /register would otherwise land them on
  // /dashboard (history pollution). Bounce them out cleanly — but NOT while we
  // are mid-submit (which logs the user in as a side-effect) or after a
  // successful registration where we keep the success screen visible.
  React.useEffect(() => {
    if (isAuthenticated && !submitting && !registered) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, submitting, registered, navigate]);  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [createdEmail, setCreatedEmail] = useState('');
  const [magicLink, setMagicLink] = useState(''); // dev-mode

  // ── Step 1: basics
  const [basics, setBasics] = useState({
    firstName: '', lastName: '', email: '', password: '', passwordConfirm: '',
    phone: '', dateOfBirth: '', userType: 'passenger',
  });

  // ── Step 2: home + frequent route
  const [home, setHome] = useState({});               // {full, street, house_number, zip, city, lat, lng}
  const [routeFrom, setRouteFrom] = useState({});
  const [routeTo, setRouteTo] = useState({});

  // ── Step 3: commuter pendler
  const [commuteEnabled, setCommuteEnabled] = useState(false);
  const [commuteStart, setCommuteStart] = useState({});
  const [commuteEnd, setCommuteEnd] = useState({});
  const [corridorKm, setCorridorKm] = useState(5);
  const [schedule, setSchedule] = useState(
    DAYS.map(d => ({ day_of_week: d.key, active: d.key < 5, time_forward: '08:00', time_return: '17:00' }))
  );

  // ── Step 4: vehicle (only for drivers/both)
  const [vehicle, setVehicle] = useState({ make: '', model: '', color: '', license_plate: '' });
  const [segmentOverride, setSegmentOverride] = useState(''); // empty = auto
  const detectedSegment = useMemo(
    () => detectSegment(vehicle.make, vehicle.model),
    [vehicle.make, vehicle.model]
  );
  const finalSegment = segmentOverride || detectedSegment || 'kompakt';
  const allMakes = useMemo(() => getAllMakes(), []);
  const modelOptions = useMemo(() => getModelsForMake(vehicle.make), [vehicle.make]);

  const isDriver = basics.userType === 'driver' || basics.userType === 'both';
  const TOTAL_STEPS = isDriver ? 6 : 5;
  const REVIEW_STEP = isDriver ? 5 : 4;
  const SUCCESS_STEP = isDriver ? 6 : 5;

  const canNext1 = basics.firstName && basics.email && basics.password &&
                   basics.password === basics.passwordConfirm &&
                   basics.phone && basics.dateOfBirth && basics.password.length >= 8;
  const canNext2 = !!home.full;
  const canNext3 = !commuteEnabled || (!!commuteStart.full && !!commuteEnd.full);
  const canNext4 = !isDriver || (!!vehicle.make && !!vehicle.model && !!vehicle.license_plate);

  const submit = async () => {
    setLoading(true);
    setSubmitting(true);
    setError(''); setErrorCode('');
    try {
      // 1) Create account (include vehicle inline for drivers — server expects this)
      const regPayload = {
        firstName: basics.firstName, lastName: basics.lastName,
        email: basics.email, password: basics.password,
        phone: basics.phone, dateOfBirth: basics.dateOfBirth,
        userType: basics.userType, preferred_lang: 'de',
      };
      if (isDriver && vehicle.make && vehicle.model && vehicle.license_plate) {
        regPayload.vehicle = {
          make: vehicle.make.trim(),
          model: vehicle.model.trim(),
          color: vehicle.color.trim(),
          licensePlate: vehicle.license_plate.trim().toUpperCase(),
          segment: finalSegment,
        };
      }
      const r = await register(regPayload);
      if (!r.success) throw new Error(typeof r.error === 'string' ? r.error : (r.error?.detail || 'register_failed'));

      // 2) Save profile (home + frequent route + commuter coords + vehicle)
      const profilePayload = {
        city: home.city || '', home_address_full: home.full,
        home_street: home.street, home_house_number: home.house_number,
        home_zip: home.zip, home_lat: home.lat, home_lng: home.lng,
        home_city: home.city || '', home_district: home.district || '',
        home_state: home.state || '', home_country: home.country || '',
        frequent_route_from: routeFrom.full || '',
        frequent_route_to: routeTo.full || '',
        commute_start_address: commuteStart.full || null,
        commute_start_lat: commuteStart.lat || null, commute_start_lng: commuteStart.lng || null,
        commute_start_street: commuteStart.street || '',
        commute_start_house_number: commuteStart.house_number || '',
        commute_start_zip: commuteStart.zip || '',
        commute_start_city: commuteStart.city || '',
        commute_start_district: commuteStart.district || '',
        commute_start_state: commuteStart.state || '',
        commute_start_country: commuteStart.country || '',
        commute_end_address: commuteEnd.full || null,
        commute_end_lat: commuteEnd.lat || null, commute_end_lng: commuteEnd.lng || null,
        commute_end_street: commuteEnd.street || '',
        commute_end_house_number: commuteEnd.house_number || '',
        commute_end_zip: commuteEnd.zip || '',
        commute_end_city: commuteEnd.city || '',
        commute_end_district: commuteEnd.district || '',
        commute_end_state: commuteEnd.state || '',
        commute_end_country: commuteEnd.country || '',
      };
      if (isDriver && vehicle.make && vehicle.model) {
        profilePayload.vehicle = {
          make: vehicle.make.trim(),
          model: vehicle.model.trim(),
          color: vehicle.color.trim(),
          license_plate: vehicle.license_plate.trim().toUpperCase(),
          segment: finalSegment,
        };
      }
      await axios.put(`${API}/auth/me`, profilePayload);

      // 3) Save commuter schedule + corridor if enabled
      if (commuteEnabled && commuteStart.full && commuteEnd.full) {
        try {
          await axios.put(`${API}/commuter/schedule`, {
            from_city: commuteStart.city || commuteStart.full,
            to_city: commuteEnd.city || commuteEnd.full,
            corridor_km: corridorKm, allow_detour: true, schedule,
          });
        } catch (_) { /* non-blocking */ }
      }

      // 4) Trigger magic link — preserve origin (orb vs webapp) so the
      //    post-PIN redirect lands on the correct app surface.
      try {
        const m = await axios.post(`${API}/auth/new-user-magic-link`, {
          email: basics.email, source,
        });
        setMagicLink(m.data?.magic_link || '');
      } catch (_) { /* dev-non-blocking */ }

      setCreatedEmail(basics.email);
      setRegistered(true);
      // Log the user out so they're FORCED through the magic-link → PIN-setup
      // flow instead of accidentally landing on /dashboard with a half-set-up
      // account. The success screen below explains the next step.
      try { await logout(); } catch (_) { /* ignore */ }
      setStep(SUCCESS_STEP);
    } catch (e) {
      const raw = e?.message || e;
      const code = typeof raw === 'string' ? raw : (raw?.detail || 'unknown');
      const key = `auth.errors.${code}`;
      const tx = t(key);
      setError(tx !== key ? tx : code);
      setErrorCode(typeof code === 'string' ? code : '');
    }
    setLoading(false);
    setSubmitting(false);
  };

  return (
    <div className="login-bg" data-testid="register-page">
      <div className="login-card-wrapper">
        <div className="login-card" data-testid="register-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Link to="/login" className="login-back">←</Link>
            <LanguageFlags />
          </div>
          <LanguageSwitcher />

          <h1 className="login-title">{t('auth.register.title')}</h1>
          <p className="login-subtitle">{t('auth.register.subtitle')}</p>

          {/* Progress dots */}
          <div style={progressBarStyle} data-testid="register-progress">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(n => (
              <div key={n} style={{
                ...progressDotStyle,
                background: n === step ? '#00d4ff' : (n < step ? '#0ea5e9' : 'rgba(255,255,255,0.15)'),
              }} />
            ))}
          </div>

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <div data-testid="register-step-1" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field icon={<User size={16} />}>
                <input data-testid="reg-firstName" placeholder={t('auth.register.firstName')}
                  value={basics.firstName} onChange={e => setBasics({ ...basics, firstName: e.target.value })} />
              </Field>
              <Field icon={<User size={16} />}>
                <input data-testid="reg-lastName" placeholder={t('auth.register.lastName')}
                  value={basics.lastName} onChange={e => setBasics({ ...basics, lastName: e.target.value })} />
              </Field>
              <Field icon={<Mail size={16} />}>
                <input data-testid="reg-email" type="email" placeholder={t('auth.register.email')}
                  value={basics.email} onChange={e => setBasics({ ...basics, email: e.target.value })} />
              </Field>
              <Field icon={<Lock size={16} />}>
                <input data-testid="reg-password" type="password" placeholder={t('auth.register.password')}
                  value={basics.password} onChange={e => setBasics({ ...basics, password: e.target.value })} />
              </Field>
              <Field icon={<Lock size={16} />}>
                <input data-testid="reg-passwordConfirm" type="password" placeholder={t('auth.register.confirmPassword')}
                  value={basics.passwordConfirm} onChange={e => setBasics({ ...basics, passwordConfirm: e.target.value })} />
              </Field>
              <Field icon={<Phone size={16} />}>
                <input data-testid="reg-phone" type="tel" placeholder={t('auth.register.phone')}
                  value={basics.phone} onChange={e => setBasics({ ...basics, phone: e.target.value })} />
              </Field>
              <Field icon={<Calendar size={16} />}>
                <input data-testid="reg-dob" type="date"
                  value={basics.dateOfBirth} onChange={e => setBasics({ ...basics, dateOfBirth: e.target.value })} />
              </Field>
              <Field icon={<Users size={16} />}>
                <select data-testid="reg-role" value={basics.userType}
                  onChange={e => setBasics({ ...basics, userType: e.target.value })}>
                  <option value="passenger">{t('auth.register.roles.passenger')}</option>
                  <option value="driver">{t('auth.register.roles.driver')}</option>
                  <option value="both">{t('auth.register.roles.both')}</option>
                </select>
              </Field>
            </div>
          )}

          {/* ── STEP 2: Heimat + Stammstrecke ── */}
          {step === 2 && (
            <div data-testid="register-step-2" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SectionTitle icon={<Home size={16} />} text={t('auth.register.homeAddress')} />
              <AddrField value={home} onChange={setHome}
                placeholder={t('auth.register.homePlaceholder')}
                testId="reg-home" />

              <SectionTitle icon={<Route size={16} />} text={t('auth.register.frequentRoute')} optional />
              <AddrField value={routeFrom} onChange={setRouteFrom}
                placeholder={t('auth.register.routeFromPlaceholder')}
                testId="reg-route-from" />
              <AddrField value={routeTo} onChange={setRouteTo}
                placeholder={t('auth.register.routeToPlaceholder')}
                testId="reg-route-to" />
            </div>
          )}

          {/* ── STEP 3: Pendler ── */}
          {step === 3 && (
            <div data-testid="register-step-3" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SectionTitle icon={<Route size={16} />} text={t('auth.register.commuter')} optional />
              <label style={toggleStyle}>
                <input type="checkbox" data-testid="reg-commute-enable"
                  checked={commuteEnabled} onChange={e => setCommuteEnabled(e.target.checked)} />
                <span>{t('auth.register.commuterEnable')}</span>
              </label>

              {commuteEnabled && (
                <>
                  <AddrField value={commuteStart} onChange={setCommuteStart}
                    placeholder={t('auth.register.commuteStartPlaceholder')}
                    testId="reg-commute-start" />
                  <AddrField value={commuteEnd} onChange={setCommuteEnd}
                    placeholder={t('auth.register.commuteEndPlaceholder')}
                    testId="reg-commute-end" />

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    <span style={{ fontSize: 13, color: '#94a3b8', minWidth: 110 }}>
                      {t('auth.register.corridor', 'Umweg-Toleranz')}
                    </span>
                    <input type="range" min={1} max={20} step={1}
                      data-testid="reg-corridor-km"
                      value={corridorKm}
                      onChange={e => setCorridorKm(parseInt(e.target.value, 10))}
                      style={{ flex: 1 }} />
                    <span style={{ fontSize: 13, color: '#00d4ff', fontWeight: 600, minWidth: 44, textAlign: 'right' }}>
                      {corridorKm} km
                    </span>
                  </div>

                  <div style={{ marginTop: 12, fontSize: 13, color: '#94a3b8' }}>
                    {t('auth.register.commuteSchedule')}
                  </div>
                  {DAYS.map((d, i) => (
                    <div key={d.key} style={dayRowStyle} data-testid={`reg-day-${d.key}`}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 70 }}>
                        <input type="checkbox" checked={schedule[i].active}
                          onChange={e => {
                            const next = [...schedule];
                            next[i] = { ...next[i], active: e.target.checked };
                            setSchedule(next);
                          }} />
                        <strong>{d.short}</strong>
                      </label>
                      {schedule[i].active && (
                        <>
                          <input type="time" value={schedule[i].time_forward}
                            data-testid={`reg-day-${d.key}-fwd`}
                            onChange={e => { const n = [...schedule]; n[i].time_forward = e.target.value; setSchedule(n); }}
                            style={timeInputStyle} />
                          <span style={{ color: '#94a3b8' }}>→</span>
                          <input type="time" value={schedule[i].time_return}
                            data-testid={`reg-day-${d.key}-ret`}
                            onChange={e => { const n = [...schedule]; n[i].time_return = e.target.value; setSchedule(n); }}
                            style={timeInputStyle} />
                        </>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ── STEP 4 (drivers only): Fahrzeug ── */}
          {step === 4 && isDriver && (
            <div data-testid="register-step-4-vehicle" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SectionTitle icon={<Car size={16} />} text={t('auth.register.vehicle', 'Dein Fahrzeug')} />

              <Field icon={<Car size={16} />}>
                <input data-testid="reg-vehicle-make" list="reg-make-list"
                  placeholder={t('auth.register.vehicleMakePlaceholder', 'Marke (z. B. VW, BMW)')}
                  value={vehicle.make}
                  onChange={e => setVehicle({ ...vehicle, make: e.target.value, model: '' })} />
              </Field>
              <datalist id="reg-make-list">
                {allMakes.map(m => <option key={m} value={m} />)}
              </datalist>

              <Field icon={<Car size={16} />}>
                <input data-testid="reg-vehicle-model" list="reg-model-list"
                  placeholder={t('auth.register.vehicleModelPlaceholder', 'Modell (z. B. Golf, Astra)')}
                  value={vehicle.model}
                  onChange={e => setVehicle({ ...vehicle, model: e.target.value })} />
              </Field>
              <datalist id="reg-model-list">
                {modelOptions.map(m => <option key={m} value={m} />)}
              </datalist>

              <Field icon={<Palette size={16} />}>
                <input data-testid="reg-vehicle-color"
                  placeholder={t('auth.register.vehicleColorPlaceholder', 'Farbe')}
                  value={vehicle.color}
                  onChange={e => setVehicle({ ...vehicle, color: e.target.value })} />
              </Field>

              <Field icon={<Hash size={16} />}>
                <input data-testid="reg-vehicle-plate"
                  placeholder={t('auth.register.vehiclePlatePlaceholder', 'Kennzeichen (z. B. M-AB 1234)')}
                  value={vehicle.license_plate}
                  onChange={e => setVehicle({ ...vehicle, license_plate: e.target.value.toUpperCase() })} />
              </Field>

              {/* Segment auto/manual */}
              <div data-testid="reg-vehicle-segment-row" style={{
                display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8,
                padding: '12px 14px', borderRadius: 12,
                background: detectedSegment ? 'rgba(34,197,94,0.08)' : 'rgba(245,158,11,0.08)',
                border: `1px solid ${detectedSegment ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)'}`,
              }}>
                <span style={{ fontSize: 13, color: '#cbd5e1' }}>
                  {detectedSegment
                    ? t('auth.register.segmentDetected', { segment: SEGMENT_LABELS[detectedSegment] || detectedSegment, defaultValue: 'Wagenklasse erkannt: {{segment}}' })
                    : t('auth.register.segmentManual', 'Wagenklasse nicht erkannt — bitte wählen:')}
                </span>
                <select data-testid="reg-vehicle-segment-select"
                  value={segmentOverride || detectedSegment || 'kompakt'}
                  onChange={e => setSegmentOverride(e.target.value === detectedSegment ? '' : e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 14,
                    background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(255,255,255,0.18)',
                    color: '#f1f5f9', outline: 'none', cursor: 'pointer', appearance: 'auto',
                  }}>
                  {Object.entries(SEGMENT_LABELS).map(([k, v]) => (
                    <option key={k} value={k} style={{ color: '#0f172a', background: '#fff' }}>{v}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* ── REVIEW ── */}
          {step === REVIEW_STEP && (
            <div data-testid="register-step-4" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#f1f5f9' }}>
                {t('auth.register.reviewTitle')}
              </h3>
              <Summary k={t('auth.register.email')} v={basics.email} />
              <Summary k={t('auth.register.firstName')} v={`${basics.firstName} ${basics.lastName}`} />
              <Summary k={t('auth.register.role')} v={t(`auth.register.roles.${basics.userType === 'passenger' ? 'passenger' : basics.userType === 'driver' ? 'driver' : 'both'}`)} />
              <Summary k={t('auth.register.homeAddress')} v={home.full} />
              {(routeFrom.full || routeTo.full) && (
                <Summary k={t('auth.register.frequentRoute')} v={`${routeFrom.full || '?'} → ${routeTo.full || '?'}`} />
              )}
              {commuteEnabled && commuteStart.full && commuteEnd.full && (
                <Summary k={t('auth.register.commuter')} v={`${commuteStart.full} → ${commuteEnd.full} (${schedule.filter(d => d.active).length} ${t('auth.register.days', 'Tage')}, ±${corridorKm} km)`} />
              )}
              {isDriver && vehicle.make && vehicle.model && (
                <Summary k={t('auth.register.vehicle', 'Fahrzeug')}
                  v={`${vehicle.make} ${vehicle.model} · ${vehicle.license_plate} · ${SEGMENT_LABELS[finalSegment] || finalSegment}`} />
              )}
              <p style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
                {t('auth.register.reviewHint')}
              </p>
            </div>
          )}

          {/* ── SUCCESS ── */}
          {step === SUCCESS_STEP && (
            <div data-testid="register-step-5" style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <MailCheck size={32} color="#fff" />
                </div>
              </div>
              <h3 style={{ margin: '0 0 6px', fontSize: 20, color: '#f1f5f9' }}>
                {t('auth.register.successTitle')}
              </h3>
              <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 14 }}>
                {t('auth.register.successText', { email: createdEmail })}
              </p>
              {magicLink && (
                <a href={magicLink} data-testid="register-dev-magic-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 22px',
                    borderRadius: 10, background: 'linear-gradient(135deg,#00d4ff,#0ea5e9)',
                    color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
                  <Check size={14} /> {t('auth.register.devOpenLink', 'Dev-Link öffnen')}
                </a>
              )}
              <p style={{ marginTop: 20, fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>
                {t('auth.register.devNote')}
              </p>
            </div>
          )}

          {/* Error box */}
          {error && (
            <div className="error-box" data-testid="register-error" style={{ marginTop: 12 }}>
              {error}
              {errorCode && <AIErrorHelper code={errorCode} testId="register-ai-helper" />}
            </div>
          )}

          {/* Navigation */}
          {step < SUCCESS_STEP && (
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              {step > 1 && (
                <button type="button" onClick={() => setStep(step - 1)}
                  data-testid="register-back" style={navBackStyle}>
                  <ArrowLeft size={16} /> {t('common.back')}
                </button>
              )}
              {step < REVIEW_STEP && (
                <button type="button" onClick={() => setStep(step + 1)}
                  disabled={
                    (step === 1 && !canNext1) ||
                    (step === 2 && !canNext2) ||
                    (step === 3 && !canNext3) ||
                    (step === 4 && !canNext4)
                  }
                  data-testid="register-next" style={{
                    ...navNextStyle,
                    opacity: ((step === 1 && !canNext1) || (step === 2 && !canNext2) || (step === 3 && !canNext3) || (step === 4 && !canNext4)) ? 0.4 : 1,
                  }}>
                  {t('common.next')} <ArrowRight size={16} />
                </button>
              )}
              {step === REVIEW_STEP && (
                <button type="button" onClick={submit} disabled={loading}
                  data-testid="register-submit" style={navNextStyle}>
                  {loading ? '...' : t('auth.register.submit')}
                </button>
              )}
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#94a3b8' }}>
            {t('auth.register.hasAccount')}{' '}
            <Link to="/login" style={{ color: '#00d4ff', textDecoration: 'none' }}>
              {t('auth.register.login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ icon, children }) => (
  <div style={{ position: 'relative' }}>
    <span style={{ position: 'absolute', left: 14, top: 14, color: '#94a3b8' }}>{icon}</span>
    {React.cloneElement(children, {
      style: {
        width: '100%', padding: '12px 16px 12px 40px', borderRadius: 12,
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
        color: '#f1f5f9', outline: 'none', fontSize: 15, boxSizing: 'border-box',
      },
    })}
  </div>
);

const SectionTitle = ({ icon, text, optional }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, color: '#cbd5e1', fontSize: 13, fontWeight: 600 }}>
    {icon} {text}
    {optional && <span style={{ marginLeft: 6, fontSize: 11, color: '#64748b' }}>(optional)</span>}
  </div>
);

const Summary = ({ k, v }) => (
  v ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px',
      background: 'rgba(255,255,255,0.04)', borderRadius: 8, fontSize: 13 }}>
      <span style={{ color: '#94a3b8' }}>{k}</span>
      <span style={{ color: '#f1f5f9', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
    </div>
  ) : null
);

/**
 * Adapter: maps the existing `<AddressAutocomplete>` (string-based, identical to
 * SearchRide/CreateRide) to the structured object shape used in the wizard
 * ({full, street, house_number, zip, city, lat, lng}).
 */
const AddrField = ({ value, onChange, placeholder, testId }) => {
  const text = value?.full || '';
  return (
    <div data-testid={`${testId}-host`}>
      <AddressAutocomplete
        value={text}
        onChange={(v) => onChange({ ...value, full: v })}
        onSelect={(s) => {
          onChange({
            full: s.display_name || s.short_name || s.poi_name || text,
            street: s.road || '',
            house_number: s.house_number || '',
            zip: s.postcode || '',
            city: s.city || s.town || s.village || '',
            district: s.district || '',
            state: s.state || '',
            country: s.country || '',
            country_code: s.country_code || '',
            lat: s.lat,
            lng: s.lng,
          });
        }}
        placeholder={placeholder}
        testId={testId}
      />
    </div>
  );
};

const progressBarStyle = { display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 18, marginTop: 8 };
const progressDotStyle = { width: 28, height: 4, borderRadius: 2, transition: 'background 0.2s' };
const navBackStyle = {
  flex: 1, padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#cbd5e1', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
};
const navNextStyle = {
  flex: 1, padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
  background: 'linear-gradient(135deg,#00d4ff,#0ea5e9)', border: 'none',
  color: '#fff', fontWeight: 600, fontSize: 14,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
};
const toggleStyle = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
  background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)',
  borderRadius: 12, color: '#86efac', fontSize: 14, cursor: 'pointer',
};
const dayRowStyle = {
  display: 'flex', gap: 8, alignItems: 'center', padding: 8,
  background: 'rgba(255,255,255,0.03)', borderRadius: 8,
};
const timeInputStyle = {
  flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 13,
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
  color: '#f1f5f9', outline: 'none',
};
